import React, { useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    
    useEffect(() => {
        document.body.classList.add('privacy-policy-open');
        
        window.scrollTo(0, 0);
        
        return () => {
            document.body.classList.remove('privacy-policy-open');
        };
    }, []);
    
    const goBack = () => {
        navigate(-1); // This will take the user back to their previous page
    };
    
    return (
        <div className="privacy-policy-page">
            {user && <Header user={user} />}
            
            <div className="privacy-policy-container">
                <div className="privacy-policy-content">
                    <div className="privacy-policy-nav">
                        <button onClick={goBack} className="back-button">
                            <span>←</span> Back
                        </button>
                    </div>
                    
                    <h1>Privacy Policy</h1>
                    <p className="last-updated">Last Updated: May 2026</p>

                    <section>
                        <h2>1. Introduction</h2>
                        <p>This Privacy Policy applies to Dannytopia, a website operated by <strong>Moon Park LLC</strong>, a Massachusetts limited liability company. Dannytopia is an adult content platform featuring nudity and mature content. This site is intended solely for individuals who are 18 years of age or older.</p>
                        <p>We are committed to handling your personal data responsibly and transparently. This policy explains what data we collect, why we collect it, how it is stored, and what rights you have over it.</p>
                    </section>

                    <section>
                        <h2>2. Age Verification</h2>
                        <p>Access to this website is restricted to individuals aged 18 or older. All users are required to complete an age verification step upon account creation before any content is accessible.</p>
                        <p>During verification, you will be asked to provide your date of birth. This date is used only to confirm that you meet the minimum age requirement. <strong>Your date of birth is not stored.</strong> Upon successful verification, we record only the following:</p>
                        <ul>
                            <li>A confirmation that age verification was completed</li>
                            <li>The timestamp at which verification occurred</li>
                        </ul>
                        <p>Users who do not meet the minimum age requirement are denied access and their session is immediately terminated. This approach minimizes the personal data we hold while maintaining a documented verification record in accordance with applicable law.</p>
                    </section>

                    <section>
                        <h2>3. Data We Collect</h2>
                        <p>We collect only the data necessary to operate this service. This includes:</p>
                        <ul>
                            <li><strong>Account Data:</strong> Your email address and display name, provided when you sign in via Google or email.</li>
                            <li><strong>Profile Data:</strong> Your username and profile image, which may be sourced from your Google account.</li>
                            <li><strong>Subscription &amp; Purchase Data:</strong> Records of your subscription status, subscription start and end dates, and any individual content purchases. We do not store your payment card details — these are handled exclusively by Stripe (see Section 9).</li>
                            <li><strong>Age Verification Record:</strong> A confirmation flag and timestamp indicating that age verification was completed. Your date of birth is not retained.</li>
                            <li><strong>Communications:</strong> Messages you send through the in-app chat feature are stored to facilitate communication between you and the site operator.</li>
                        </ul>
                        <p>We do not collect your IP address, browser fingerprint, or device information directly. Some of this data may be collected passively by our third-party infrastructure providers (see Section 9).</p>
                    </section>

                    <section>
                        <h2>4. How We Collect Your Data</h2>
                        <ul>
                            <li><strong>Direct interactions.</strong> You provide data when you create an account, complete age verification, subscribe, make a purchase, or contact us.</li>
                            <li><strong>Third-party sign-in.</strong> When you sign in with Google, we receive your email address and display name from Google's authentication service.</li>
                            <li><strong>Automated collection by third parties.</strong> Our infrastructure providers (Firebase, Stripe) may collect technical data such as IP addresses and browser information as part of their services. This is governed by their respective privacy policies.</li>
                        </ul>
                    </section>

                    <section>
                        <h2>5. How We Use Your Data</h2>
                        <ul>
                            <li>To create and manage your account.</li>
                            <li>To verify that you meet the minimum age requirement.</li>
                            <li>To process and fulfill your subscription or content purchases.</li>
                            <li>To facilitate communication between you and the site operator.</li>
                            <li>To maintain records required for legal or accounting purposes.</li>
                            <li>To protect the security and integrity of the platform.</li>
                        </ul>
                        <p>We do not sell your personal data to third parties. We do not use your data for advertising purposes.</p>
                    </section>

                    <section>
                        <h2>6. Data Security</h2>
                        <p>Your personal data is stored in Google Firebase, a cloud platform that maintains industry-standard security certifications including SOC 2 and ISO 27001. Access to user data is restricted to the site owner (Moon Park LLC). We do not employ staff with access to your personal information.</p>
                        <p>While we take reasonable precautions to protect your data, no system is completely secure. In the event of a data breach that affects your personal information, we will notify you as required by applicable law.</p>
                    </section>

                    <section>
                        <h2>7. Data Retention</h2>
                        <p>We retain your personal data for as long as your account is active or as needed to provide you with our services. If you request deletion of your account, we will remove your personal data from our systems within a reasonable time, except where retention is required by law (for example, financial records related to subscription payments).</p>
                    </section>

                    <section>
                        <h2>8. Your Rights</h2>
                        <p>As a user of this platform, you have the right to:</p>
                        <ul>
                            <li>Request a copy of the personal data we hold about you.</li>
                            <li>Request correction of inaccurate data.</li>
                            <li>Request deletion of your personal data.</li>
                            <li>Withdraw consent at any time by closing your account.</li>
                        </ul>
                        <p>Massachusetts residents have additional rights under 201 CMR 17.00, including the right to be informed of any unauthorized access to their personal data. To exercise any of these rights, please contact us at <strong>dannytopia.llc@gmail.com</strong>.</p>
                    </section>

                    <section>
                        <h2>9. Third-Party Services</h2>
                        <p>We use the following third-party services to operate this platform:</p>
                        <ul>
                            <li><strong>Google Firebase</strong> — authentication, database storage, and file hosting. Privacy policy: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">firebase.google.com/support/privacy</a></li>
                            <li><strong>Stripe</strong> — payment processing. Stripe collects and stores your payment card details directly; Moon Park LLC does not have access to your full card information. Privacy policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
                            <li><strong>Google Sign-In (OAuth)</strong> — account authentication. Subject to Google's Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
                        </ul>
                    </section>

                    <section>
                        <h2>10. Cookies &amp; Local Storage</h2>
                        <p>This website does not use first-party tracking cookies. Firebase Authentication uses browser local storage to maintain your login session. Stripe may set cookies as part of the payment process. We do not use cookies for advertising or analytics purposes.</p>
                    </section>

                    <section>
                        <h2>11. Changes to This Policy</h2>
                        <p>We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date at the top of this page. Continued use of the site after changes are posted constitutes your acceptance of the updated policy. For significant changes, we will make reasonable efforts to notify you directly.</p>
                    </section>

                    <section>
                        <h2>12. Contact Us</h2>
                        <p>If you have any questions about this Privacy Policy or how we handle your data, please contact:</p>
                        <p><strong>Moon Park LLC</strong><br />Email: <a href="mailto:dannytopia.llc@gmail.com">dannytopia.llc@gmail.com</a></p>
                    </section>
                    
                    <div className="privacy-policy-nav bottom-nav">
                        <button onClick={goBack} className="back-button">
                            <span>←</span> Back
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy; 